import { motion } from 'framer-motion';
import { ArrowLeft, ClipboardList, Volleyball, HeartHandshake } from 'lucide-react';
import { Logo } from '@/components/Logo';

export type SignupPath = 'player' | 'coach' | 'parent';

interface RoleChoiceScreenProps {
  onChoose: (role: SignupPath) => void;
  onBack?: () => void;
}

const CARDS: {
  id: SignupPath;
  Icon: typeof Volleyball;
  title: string;
  subline: string;
  accent: string;
  badge?: string;
}[] = [
  {
    id: 'player',
    Icon: Volleyball,
    title: "I'm a Player",
    subline: 'I want to learn sports strategy and build my game IQ',
    accent: 'primary',
  },
  {
    id: 'coach',
    Icon: ClipboardList,
    title: "I'm a Coach",
    subline: 'I want to train my team and assign lessons to my players',
    accent: 'coach',
    badge: 'Start here',
  },
  {
    id: 'parent',
    Icon: HeartHandshake,
    title: "I'm a Parent",
    subline: 'I want to sign my child up and track their progress',
    accent: 'parent',
  },
];

const ACCENT: Record<string, { border: string; hover: string; chip: string; icon: string; badge: string }> = {
  primary: {
    border: 'border-primary/30', hover: 'hover:border-primary',
    chip: 'bg-primary/15', icon: 'text-primary', badge: 'bg-primary text-primary-foreground',
  },
  coach: {
    border: 'border-coach/40', hover: 'hover:border-coach',
    chip: 'bg-coach/15', icon: 'text-coach', badge: 'bg-coach text-coach-foreground',
  },
  parent: {
    border: 'border-parent/40', hover: 'hover:border-parent',
    chip: 'bg-parent/15', icon: 'text-parent', badge: 'bg-parent text-parent-foreground',
  },
};

/**
 * Full-screen split entry point shown before any signup form.
 * Routes brand-new users into the player, coach, or parent flow.
 */
export function RoleChoiceScreen({ onChoose, onBack }: RoleChoiceScreenProps) {
  return (
    <div className="min-h-screen premium-page-bg flex flex-col items-center justify-center px-4 py-10">
      {onBack && (
        <button
          onClick={onBack}
          className="absolute left-4 top-5 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
      )}

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-5xl text-center"
      >
        <Logo className="w-16 h-16 mx-auto" alt="Sportara" />
        <h1 className="font-display font-bold text-3xl sm:text-4xl mt-5 tracking-tight">
          Who are you joining as?
        </h1>
        <p className="text-sm text-muted-foreground font-body mt-2">
          Pick the path that fits you — we'll set everything else up for you.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          {CARDS.map(({ id, Icon, title, subline, accent, badge }) => {
            const a = ACCENT[accent];
            return (
              <motion.button
                key={id}
                whileHover={{ y: -4 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onChoose(id)}
                className={`group relative text-left rounded-3xl border-2 ${id === 'coach' ? 'border-coach ring-2 ring-coach/30' : a.border} ${a.hover} bg-card/60 backdrop-blur-xl p-6 sm:p-7 transition-colors shadow-lg`}
              >
                {badge && (
                  <span className={`absolute top-4 right-4 text-[10px] font-display font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${a.badge}`}>
                    {badge}
                  </span>
                )}
                <div className={`w-14 h-14 rounded-2xl ${a.chip} flex items-center justify-center mb-4`}>
                  <Icon className={`w-7 h-7 ${a.icon}`} />
                </div>
                <p className="font-display font-bold text-xl">{title}</p>
                <p className="text-sm text-muted-foreground font-body mt-1.5">{subline}</p>
              </motion.button>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}

export default RoleChoiceScreen;
